import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from '@/utils/constants';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IUploadFileProps {
  accept?: string;
  maxFileSize?: number;
  disabled?: boolean;
  multiple?: boolean;
  setFiles?: (file: File[]) => void;
}

const isFileAccepted = (file: File, accept: string) => {
  const acceptedTypes = accept.split(',').map((type) => type.trim());
  const fileType = file.type;
  const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
  return acceptedTypes.some(
    (type) => type === fileType || type === fileExtension,
  );
};

/**
 * Custom hook for handling file uploads via input or drag-and-drop.
 * Provides validation for file type and size, drag state, and error messages.
 * @param props - Configuration object for the upload behavior.
 * @param props.accept - Comma-separated string of accepted MIME types or file extensions (e.g., "image/png,.jpg").
 * @param props.maxFileSize - Maximum allowed file size in bytes.
 * @param props.disabled - Whether file input and drag-and-drop are disabled.
 * @param props.multiple - Whether selecting/dropping multiple files is allowed. Defaults to true. When false, the input loses the `multiple` attribute and selecting more than one file produces an error.
 * @param props.setFiles - Callback invoked with validated files.
 * @returns An object containing:
 *  - isDragging: Boolean indicating if a file is being dragged over the drop area.
 *  - error: Error message string if validation fails.
 *  - ref: Ref to attach to the file input element.
 *  - handleFileChange: Handler for input file selection.
 *  - handleDragOver: Handler for drag-over events.
 *  - handleDragLeave: Handler for drag-leave events.
 *  - handleDrop: Handler for drop events.
 */
export const useUploadFile = ({
  maxFileSize = MAX_FILE_SIZE,
  disabled = false,
  multiple = true,
  setFiles,
  accept = ALLOWED_FILE_TYPES,
}: IUploadFileProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!disabled && ref.current) {
      ref.current.value = '';
    }
  }, [disabled]);

  const validateFiles = (files: File[]) => {
    if (!multiple && files.length > 1) {
      setError(t('only-one-file-allowed'));
      return;
    }
    const validFiles = files.filter((file) => {
      if (maxFileSize && file.size > maxFileSize) {
        setError(
          t('file-size-exceeded', {
            'file.name': file.name,
            maxFileSize: maxFileSize / 1024 / 1024,
          }),
        );
        return false;
      }
      if (accept && !isFileAccepted(file, accept)) {
        setError(
          t('file-type-not-supported', { 'file.name': file.name, accept }),
        );
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setError(null);
      setFiles?.(validFiles);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const selectedFiles = Array.from(event.target.files || []);
    validateFiles(selectedFiles);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    if (disabled) return;
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(event.dataTransfer.files);
    validateFiles(droppedFiles);
  };

  return {
    isDragging,
    error,
    ref,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
};
